import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useCourses, useUpdateCourse } from '../../services/courses';
import { cn } from '@/lib/utils';

interface PublishSwapDialogProps {
  /** The course the member tried to publish. */
  courseId: string;
  courseTitle: string;
  /** The plan-limit message from the server (names the plan and the cap). */
  limitMessage: string;
  onClose: () => void;
  /** Called after the swap fully succeeds (old unpublished, new published). */
  onSwapped?: () => void;
}

/**
 * Publish-at-the-cap escape hatch (Aleix, 2026-08-10): instead of a dead-end
 * 403, let the admin pick a currently-published course to move to drafts and
 * publish the new one in its place — one decision, one click.
 */
const PublishSwapDialog: React.FC<PublishSwapDialogProps> = ({
  courseId,
  courseTitle,
  limitMessage,
  onClose,
  onSwapped,
}) => {
  const { data } = useCourses({ status: 'published', per_page: 50 });
  const published = (data?.courses ?? []).filter((c) => c.id !== courseId);
  const updateCourse = useUpdateCourse();
  const [picked, setPicked] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const swap = async () => {
    if (!picked) return;
    setWorking(true);
    try {
      await updateCourse.mutateAsync({ courseId: picked, data: { status: 'draft' } });
      try {
        await updateCourse.mutateAsync({ courseId, data: { status: 'published' } });
      } catch (err) {
        // Roll the first half back — never leave the member with one course
        // silently unpublished and nothing published in exchange.
        await updateCourse
          .mutateAsync({ courseId: picked, data: { status: 'published' } })
          .catch(() => {});
        throw err;
      }
      toast.success(`Swapped — “${courseTitle}” is now published.`);
      onSwapped?.();
      onClose();
    } catch {
      toast.error('The swap did not complete — nothing was changed.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !working) onClose();
      }}
    >
      <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-border-primary bg-surface-primary p-6">
        <h2 className="text-lg font-bold text-text-primary">Your plan is at its limit</h2>
        <p className="text-sm text-text-secondary">
          {limitMessage} To publish{' '}
          <span className="font-semibold text-text-primary">“{courseTitle}”</span> now,
          choose a published course to move to drafts (learners lose access to it
          until it&rsquo;s re-published — progress is kept).
        </p>

        <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
          {published.map((c) => (
            <label
              key={c.id}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors duration-150',
                picked === c.id
                  ? 'border-primary-500/60 bg-primary-500/5 dark:bg-primary-350/10'
                  : 'border-border-primary hover:border-primary-300'
              )}
            >
              <input
                type="radio"
                name="swap-course"
                checked={picked === c.id}
                onChange={() => setPicked(c.id)}
                className="h-4 w-4 accent-primary-500"
              />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                {c.title}
              </span>
            </label>
          ))}
          {published.length === 0 && (
            <p className="text-sm text-text-tertiary">No other published courses found.</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={working}
            className="rounded-full border border-text-primary/30 px-4 py-2 text-sm font-bold text-text-primary transition-[background-color,transform] duration-150 hover:bg-text-primary/[0.06] active:scale-[0.97]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={swap}
            disabled={!picked || working}
            className="rounded-full bg-primary-500 px-4 py-2 text-sm font-bold transition-[background-color,transform] duration-150 hover:bg-primary-600 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-primary-400"
          >
            {working ? 'Swapping…' : 'Move it to drafts & publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishSwapDialog;
